<script setup>
/**
 * 页面加载进度条组件
 *
 * 功能说明：
 * - 在页面顶部显示加载进度条
 * - 支持自动递增进度（模拟加载效果）
 * - 支持手动控制开始/结束
 * - 完成时带有淡出动画效果
 *
 * 使用方式：
 * 1. 在 App.vue 中引入并挂载
 * 2. 通过 ref 调用 start() 和 done() 方法
 * 3. 或通过 eventBus 触发 'progress:start' 和 'progress:done' 事件
 */
import { ref, onMounted, onUnmounted } from 'vue'
import eventBus from '@/utils/eventBus.js'

// ==================== 响应式状态 ====================

/** 进度条是否可见 */
const visible = ref(false)

/** 当前进度值 (0-100) */
const progress = ref(0)

/** 是否正在完成动画中（用于淡出效果） */
const finishing = ref(false)

// ==================== 内部变量 ====================

/** 自动递增定时器ID */
let incrementTimer = null

/** 完成后隐藏的定时器ID */
let hideTimer = null

// ==================== 核心方法 ====================

/**
 * 开始加载进度条
 * - 重置状态并显示进度条
 * - 启动自动递增定时器，模拟加载进度
 */
const start = () => {
  // 清除之前可能存在的定时器
  clearTimers()

  // 重置状态
  visible.value = true
  progress.value = 0
  finishing.value = false

  // 启动自动递增
  // 进度递增策略：越接近100%，递增越慢，模拟真实加载体验
  incrementTimer = setInterval(() => {
    if (progress.value < 90) {
      // 随机递增 1-5%，但不超过 90%
      const increment = Math.random() * 4 + 1
      progress.value = Math.min(progress.value + increment, 90)
    }
  }, 200)
}

/**
 * 完成加载进度条
 * - 快速将进度推进到 100%
 * - 播放淡出动画后隐藏
 */
const done = () => {
  // 清除递增定时器
  if (incrementTimer) {
    clearInterval(incrementTimer)
    incrementTimer = null
  }

  // 快速完成到 100%
  progress.value = 100

  // 延迟开始淡出，让用户看到完成状态
  hideTimer = setTimeout(() => {
    finishing.value = true

    // 再延迟隐藏，等待淡出动画完成
    setTimeout(() => {
      visible.value = false
      progress.value = 0
      finishing.value = false
    }, 500)
  }, 300)
}

/**
 * 清除所有定时器
 * 用于组件卸载时的清理工作
 */
const clearTimers = () => {
  if (incrementTimer) {
    clearInterval(incrementTimer)
    incrementTimer = null
  }
  if (hideTimer) {
    clearTimeout(hideTimer)
    hideTimer = null
  }
}

// ==================== 生命周期 ====================

onMounted(() => {
  // 监听事件总线，支持全局调用
  eventBus.on('progress:start', start)
  eventBus.on('progress:done', done)
})

onUnmounted(() => {
  // 清理定时器和事件监听
  clearTimers()
  eventBus.off('progress:start', start)
  eventBus.off('progress:done', done)
})

// ==================== 暴露方法 ====================

defineExpose({
  start,
  done,
})
</script>

<template>
  <!-- 
    进度条容器
    - 固定在页面顶部
    - z-index 设置较高确保始终可见
    - 通过 visible 控制显示/隐藏
  -->
  <div v-show="visible" class="page-progress-container" :class="{ 'is-finishing': finishing }">
    <!-- 
      进度条本体
      - 宽度由 progress 值动态控制
      - 带有渐变色和光晕效果
    -->
    <div class="page-progress-bar" :style="{ width: `${progress}%` }">
      <!-- 进度条末端的高亮光点，增强视觉效果 -->
      <div class="page-progress-glow"></div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
/**
 * 进度条样式
 * 
 * 设计要点：
 * 1. 使用主题色（Ant Design Vue 的 primary color）
 * 2. 带有微妙的光晕效果增强视觉层次
 * 3. 平滑的过渡动画
 * 4. 完成时的淡出效果
 */

.page-progress-container {
  // 固定定位在页面最顶部
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9999;

  // 容器高度 - 加粗更明显
  height: 3px;

  // 背景透明，只显示进度条本身
  background: transparent;

  // 指针事件穿透，不影响页面交互
  pointer-events: none;

  // 淡出动画
  opacity: 1;
  transition: opacity 0.5s ease;

  // 完成状态：淡出
  &.is-finishing {
    opacity: 0;
  }
}

.page-progress-bar {
  // 进度条高度
  height: 100%;

  // 使用更鲜艳的颜色，确保可见性
  // 主色调：明亮的蓝色渐变
  background: linear-gradient(90deg, #1677ff 0%, #4096ff 50%, #69b1ff 100%);

  // 圆角末端
  border-radius: 0 2px 2px 0;

  // 宽度变化的过渡动画
  transition: width 0.25s ease-out;

  // 相对定位，用于放置光点
  position: relative;

  // 更强的阴影效果，增加可见性
  box-shadow:
    0 0 15px rgba(22, 119, 255, 0.8),
    0 0 8px rgba(22, 119, 255, 0.6),
    0 2px 4px rgba(22, 119, 255, 0.4);
}

.page-progress-glow {
  // 定位在进度条末端
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);

  // 光点尺寸 - 更大更明显
  width: 100px;
  height: 100%;

  // 更强的渐变光晕效果
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.5) 50%,
    rgba(255, 255, 255, 0.8) 100%
  );

  // 圆角
  border-radius: 0 2px 2px 0;

  // 脉冲动画，吸引注意力
  animation: pulse 1s ease-in-out infinite;
}

// 脉冲动画
@keyframes pulse {
  0%,
  100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}
</style>
